/**
 * useCountryProfile / useFormatters
 *
 * ANZ plan WP-3..6 (docs/Austrialia-New_Zealand/PLAN.md): the web app formats
 * money, numbers, dates, times, and phone numbers from the company's country
 * profile instead of hardcoded en-US/USD. This hook is the single place that
 * resolves the company's CountryProfile from the ['company'] query and hands
 * out bound formatter functions.
 *
 * Usage in a component:
 *   const { money, date, dateTime, phone } = useFormatters()
 *   money(quote.total)                       // "$1,234.56" / "A$1,234.56" / etc.
 *   date(quote.issueDate)                     // respects DMY/MDY per country
 *   dateTime(job.startTime)                   // uses the company's time zone
 *
 * For code outside React (CSV/xlsx export utils, plain functions passed a
 * profile), use `getFormattersFor(profile, timeZone)` directly — no hook
 * rules apply, and it never triggers a fetch.
 */
import { useMemo } from 'react'

import { useCompany } from '@/hooks/useCompany'

import {
  resolveCountryProfile,
  resolveMeasurementSystem,
  unitLabels,
  formatDistanceFromMeters,
  formatMoney,
  formatNumber,
  formatDate,
  formatDateTime,
  formatTime,
  normalizePhone,
  formatPhone,
  type CountryProfile,
  type CountryCode,
  type MeasurementSystem,
  type UnitLabels,
  type FormatMoneyOptions,
  type FormatNumberOptions,
  type FormatDateOptions,
  type FormatDateTimeOptions,
  type FormatTimeOptions,
} from '@shared/country-profiles'

type DateInput = Date | string | number | null | undefined

export interface UseCountryProfileResult {

  /** Full resolved profile — always defined, falls back to the US profile while loading or on error. */
  profile: CountryProfile
  countryCode: CountryCode

  /** Company's saved IANA time zone, or the profile's defaultTimeZone if unset. */
  timeZone: string
  locale: string
  currency: string
  isLoading: boolean
}

/**
 * Resolves the company's CountryProfile from the shared `['company']` query.
 * Safe to call from multiple components — React Query dedupes the request.
 * Falls back to the US profile while the query is loading or has no data yet.
 */
export function useCountryProfile(): UseCountryProfileResult {
  const { data, isLoading } = useCompany()

  const profile = useMemo(() => resolveCountryProfile(data ?? undefined), [data])
  const timeZone = data?.timeZone || profile.defaultTimeZone

  return {
    profile,
    countryCode: profile.code,
    timeZone,
    locale: profile.locale,
    currency: profile.currency,
    isLoading,
  }
}

export interface UseMeasurementSystemResult {
  /** Effective system: company.fleetDefaultMeasurementSystem override, else the country's default. */
  system: MeasurementSystem
  /** Whether the effective system came from an explicit company override (vs following the country). */
  isOverride: boolean
  /** What the company's country implies, ignoring any override — used to show "Follow country (Metric/Imperial)". */
  countryDefault: MeasurementSystem
  labels: UnitLabels
  isLoading: boolean
}

/**
 * Resolves the effective imperial/metric measurement system for the company:
 * `company.fleetDefaultMeasurementSystem` ('imperial'|'metric') overrides;
 * null/unset follows the country (shared/country-profiles.ts resolveMeasurementSystem).
 */
export function useMeasurementSystem(): UseMeasurementSystemResult {
  const { data, isLoading } = useCompany()
  const profile = useMemo(() => resolveCountryProfile(data ?? undefined), [data])
  const override = (data as any)?.fleetDefaultMeasurementSystem ?? null

  const system = useMemo(() => resolveMeasurementSystem(profile.code, override), [profile, override])
  const labels = useMemo(() => unitLabels(system), [system])

  return {
    system,
    isOverride: override === 'imperial' || override === 'metric',
    countryDefault: profile.measurementSystem,
    labels,
    isLoading,
  }
}

/** Convenience alias — same result as useMeasurementSystem(), shorter name for call sites that just need units. */
export const useUnits = useMeasurementSystem

export { formatDistanceFromMeters }

export interface Formatters {
  money: (amount: number | string | null | undefined, opts?: FormatMoneyOptions) => string
  number: (n: number | string | null | undefined, opts?: FormatNumberOptions) => string
  date: (input: DateInput, opts?: FormatDateOptions) => string
  dateTime: (input: DateInput, opts?: FormatDateTimeOptions) => string
  time: (input: DateInput, opts?: FormatTimeOptions) => string

  /** Formats a raw phone string for display, e.g. "(555) 123-4567" or "04xx xxx xxx". */
  phone: (raw: string | null | undefined) => string

  /** Normalizes a raw phone string to E.164 for tel:/sms: links. */
  normalizePhone: (raw: string | null | undefined) => string | null
}

/**
 * Builds bound formatter functions for a given profile/time-zone without
 * using React hooks — for CSV/xlsx export utilities and other non-component
 * code. Components should prefer `useFormatters()`.
 */
export function getFormattersFor(profile: CountryProfile, timeZone?: string): Formatters {
  const tz = timeZone ?? profile.defaultTimeZone

  
return {
    money: (amount, opts) => formatMoney(amount, profile, opts),
    number: (n, opts) => formatNumber(n, profile, opts),
    date: (input, opts) => formatDate(input, profile, { timeZone: tz, ...opts }),
    dateTime: (input, opts) => formatDateTime(input, profile, { timeZone: tz, ...opts }),
    time: (input, opts) => formatTime(input, profile, { timeZone: tz, ...opts }),
    phone: raw => formatPhone(raw, profile),
    normalizePhone: raw => normalizePhone(raw, profile),
  }
}

/**
 * Hook form of getFormattersFor — resolves the company's profile/time zone
 * internally and returns bound formatters plus a loading flag. This is what
 * most components should use.
 */
export function useFormatters(): Formatters & { isLoading: boolean } {
  const { profile, timeZone, isLoading } = useCountryProfile()
  const formatters = useMemo(() => getFormattersFor(profile, timeZone), [profile, timeZone])

  
return { ...formatters, isLoading }
}
